import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Ruler, Plus } from "lucide-react";
import { categoryLabel } from "@/lib/measurementCategoryMap";

/**
 * Iter57.2 — "Reuse existing measurement" prompt.
 *
 * Renders BEFORE the Google Workspace opens on a per-line entry when
 * (a) the invoking line has a known category AND (b) at least one saved
 * measurement on this property matches that category. Lets the user pick
 * an existing measurement (`[Reuse]`) or start fresh (`[Create New]`).
 *
 * "[Reuse]" doesn't link on its own — it opens the workspace focused on
 * the chosen polygon with the corresponding card pre-selected, so the
 * user visually verifies and confirms via the normal `Add to Estimate`
 * button. This mirrors the flow the user approved.
 */
export default function ReuseMeasurementDialog({
  open,
  onCancel,
  onReuse,       // (measurement) => void — parent opens workspace focused on it
  onCreateNew,   // () => void — parent opens workspace fresh
  propertyAddress,
  serviceCategory,
}) {
  const [items, setItems] = useState(null); // null=loading, []=empty, [...]=results

  useEffect(() => {
    if (!open || !propertyAddress || !serviceCategory) { setItems(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/measurements?property_address=${encodeURIComponent(propertyAddress)}&service_category=${encodeURIComponent(serviceCategory)}&include_usage=true`
        );
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, propertyAddress, serviceCategory]);

  // Auto-skip: if no candidates found, don't waste a click — go straight to Create New.
  useEffect(() => {
    if (open && Array.isArray(items) && items.length === 0) {
      onCreateNew?.();
    }
  }, [open, items]); // eslint-disable-line

  if (!open) return null;

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-lg" data-testid="reuse-measurement-dialog">
        <DialogHeader>
          <DialogTitle>Reuse Existing Measurement</DialogTitle>
          <DialogDescription>
            {items === null ? "Checking for saved measurements on this property…"
              : items.length === 1
                ? "You already have a saved measurement for this service on this property. Reuse it or draw a new one."
                : `You have ${items.length} saved measurements for this service on this property. Reuse one or draw a new one.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="reuse-measurement-list">
          {(items || []).map((m) => (
            <ReuseCard key={m.id} measurement={m} onReuse={() => onReuse?.(m)} />
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onCreateNew}
            data-testid="reuse-measurement-create-new"
            className="sm:mr-auto"
          >
            <Plus className="w-4 h-4 mr-1" /> Create New Measurement
          </Button>
          <Button variant="ghost" onClick={onCancel} data-testid="reuse-measurement-cancel">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReuseCard({ measurement: m, onReuse }) {
  const value = m.square_feet != null ? `${Math.round(m.square_feet).toLocaleString()} sq ft`
    : m.linear_feet != null ? `${Math.round(m.linear_feet).toLocaleString()} ft`
    : m.metric_value != null ? `${Math.round(m.metric_value)} m`
    : "—";
  const measuredOn = m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const usage = m.linked_estimates || [];
  return (
    <div
      className="border border-zinc-200 rounded-lg p-3 flex items-start gap-3 hover:border-[#FF5A00] transition-colors"
      data-testid={`reuse-measurement-card-${m.id}`}
    >
      <div className="w-10 h-10 rounded bg-orange-50 border border-orange-200 flex items-center justify-center shrink-0">
        <Ruler className="w-5 h-5 text-[#FF5A00]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-base">
          {m.notes || `${categoryLabel(m.service_category) || "Measurement"} Area`}
        </div>
        <div className="text-sm text-zinc-600">{value}</div>
        {measuredOn && <div className="text-xs text-zinc-500">Measured {measuredOn}</div>}
        {usage.length > 0 && (
          <div className="mt-1">
            <div className="text-[10px] uppercase tracking-widest font-mono-accent text-zinc-500">Used on</div>
            <ul className="text-xs text-zinc-700 list-disc list-inside">
              {usage.map((e) => (
                <li key={e.id} data-testid={`reuse-measurement-usage-${m.id}-${e.id}`}>
                  {e.estimate_number || `#${e.id.slice(-6)}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Button
        onClick={onReuse}
        className="bg-[#FF5A00] hover:bg-[#E04F00] text-white shrink-0"
        data-testid={`reuse-measurement-reuse-btn-${m.id}`}
      >
        Reuse
      </Button>
    </div>
  );
}
